import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';
import analytics from '../services/AnalyticsService';
import './ShareableProfileCard.css';

interface ShareableProfileCardProps {
  onClose: () => void;
}

const ShareableProfileCard: React.FC<ShareableProfileCardProps> = ({ onClose }) => {
  const { user } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);

  if (!user || !user.profile) {
    return null;
  }

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      analytics.trackShare('profile_card', 'download');

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${user.name || 'profile'}-twoHim.png`;
          link.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Failed to generate image:', error);
      alert('Failed to generate image. Please try again.');
    }
  };

  const handleShareToInstagram = async () => {
    analytics.trackShare('profile_card', 'instagram');

    // Instagram doesn't have a direct share API, so download first
    await handleDownload();
    alert('Image downloaded! Now open Instagram and upload it to your Story 📱');
  };

  const handleShareToTwitter = () => {
    analytics.trackShare('profile_card', 'twitter');

    const text = `Check out my profile on Two Him! 💜 ${user.profile?.bio || 'Looking for my match'} #TwoHim #Dating`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = () => {
    analytics.trackShare('profile_card', 'copy_link');

    // For now, copy the app URL. Later you can add direct profile links
    const appUrl = window.location.origin;
    navigator.clipboard.writeText(appUrl);
    alert('Link copied to clipboard! Share it anywhere 🔗');
  };

  // Get top 3 hashtags
  const topHashtags = user.profile.hashtags?.slice(0, 3) || [];

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>

        <h2>Share Your Profile</h2>
        <p className="share-subtitle">Download and share to Instagram Stories</p>

        {/* The actual card that will be captured */}
        <div className="card-preview">
          <div ref={cardRef} className="profile-share-card">
            {/* Gradient Background */}
            <div className="card-gradient"></div>

            {/* Profile Content */}
            <div className="card-content">
              <div className="card-avatar-section">
                <Avatar
                  photoUrl={user.profile.photoUrl}
                  name={user.name}
                  size="xlarge"
                />
              </div>

              <div className="card-info-section">
                <h3 className="card-name">
                  {user.name || 'Anonymous'}
                  {user.profile.age && <span className="card-age">, {user.profile.age}</span>}
                </h3>

                {user.profile.variant && (
                  <div className="card-variant">
                    {user.profile.variant.name}
                  </div>
                )}

                {user.profile.bio && (
                  <p className="card-bio">"{user.profile.bio}"</p>
                )}

                {topHashtags.length > 0 && (
                  <div className="card-hashtags">
                    {topHashtags.map((uh: any) => (
                      <span key={uh.hashtag.id} className="card-hashtag">
                        {uh.hashtag.displayName}
                      </span>
                    ))}
                  </div>
                )}

                {user.profile.location && (
                  <div className="card-location">
                    📍 {user.profile.location}
                  </div>
                )}
              </div>

              {/* App Branding */}
              <div className="card-branding">
                <div className="card-logo">Two Him</div>
                <div className="card-cta">Find Your Match 💜</div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Actions */}
        <div className="share-actions">
          <button className="share-btn instagram-btn" onClick={handleShareToInstagram}>
            📸 Share to Instagram Story
          </button>

          <button className="share-btn download-btn" onClick={handleDownload}>
            💾 Download Image
          </button>

          <button className="share-btn twitter-btn" onClick={handleShareToTwitter}>
            🐦 Share to Twitter
          </button>

          <button className="share-btn copy-link-btn" onClick={handleCopyLink}>
            🔗 Copy Link
          </button>
        </div>

        <p className="share-tip">
          💡 Tip: Share on your Instagram Story to get more matches!
        </p>
      </div>
    </div>
  );
};

export default ShareableProfileCard;
